import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@oniratec/onira-react-ui';
import {
  ChevronRightIcon,
  LogOutIcon,
  PanelLeftCloseIcon,
  PanelLeftOpenIcon,
  Settings,
} from 'lucide-react';
import { Link } from 'react-router';
import { useLocation, useNavigate } from 'react-router';
import { JSX, useEffect, useState } from 'react';
import { NavigationLink } from '../NavigationLink';
import { useNavBarStore } from '@/store/nav-bar.store';
import { usePermissions } from '@/modules/users/hooks/usePermissions';
import { useAuthStore } from '@/store/auth.store';
import { cn } from '@/lib/utils/cn';
import { buildNavigationRoutes } from '@/modules/navigation/components/main-navigation/navigation.routes';
import { useTranslations } from '@/lib/i18n/useTranslations';
import { NotificationsBell } from '@/modules/notifications/components';
import { ProfileAvatar } from '@/modules/users/components/ProfileAvatar';
import { useBrandStore } from '@/lib/store/brand.store';
import { useLongPressToOpenDebug } from '@/lib/debug/activation';
import { isDebugPanelEnabled } from '@/lib/debug/enabled';
import { BrandLogo } from '@/modules/settings/brand/components/BrandLogo';

function LogoWithLongPress({
  brand,
  isTextVisible,
}: {
  brand: { logoUrl?: string; name?: string; legalName?: string } | null;
  isTextVisible: boolean;
}) {
  const longPressProps = useLongPressToOpenDebug();

  return (
    <Link
      to={'/dashboard'}
      className={cn(
        'flex min-w-0 max-w-[140px] items-center justify-center transition-opacity duration-300',
        isTextVisible ? 'opacity-0' : 'opacity-100',
      )}
      {...(isDebugPanelEnabled() ? longPressProps : {})}
    >
      <BrandLogo
        name={brand?.name || brand?.legalName}
        logoUrl={brand?.logoUrl}
        variant="navigation"
      />
    </Link>
  );
}

export const MainNavigation = () => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isTextVisible, setIsTextVisible] = useState(true);
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({});
  const { isMainNavCollapsed, setIsMainNavCollapsed } = useNavBarStore();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { canRead } = usePermissions();
  const user = useAuthStore(s => s.user);
  const brand = useBrandStore(s => s.brand);
  const logout = useAuthStore(state => state.logout);
  const t = useTranslations('Navigation');
  const canSeeNotifications = canRead('NOTIFICATIONS');

  const toggleSidebar = () => {
    setIsCollapsed(!isCollapsed);
    setIsMainNavCollapsed(!isMainNavCollapsed);
  };

  const visibleNavigation = buildNavigationRoutes(pathname, {
    dashboard: t('dashboard'),
    services: t('services'),
    incidences: t('incidences'),
    inventory: t('inventory'),
    products: t('products'),
    stock: t('stock'),
    consolidatedStock: t('consolidatedStock'),
    serialNumbers: t('serialNumbers'),
    users: t('users'),
    customers: t('customers'),
    billing: t('billing'),
    quotes: t('quotes'),
    invoices: t('invoices'),
    deliveryNotes: t('deliveryNotes'),
    accounting: t('accounting'),
    purchasing: t('purchasing'),
    suppliers: t('suppliers'),
    purchaseInvoices: t('purchaseInvoices'),
    workOrders: t('workOrders'),
    calendar: t('calendar'),
    finance: t('finance'),
    accountsPayable: t('accountsPayable'),
  }).filter(item => {
    if (canRead(item.module)) {
      return true;
    }

    if (item.children?.length) {
      return item.children.some(child => canRead(child.module));
    }

    return false;
  });

  const dropdownNavigation: Array<{
    href: string;
    target?: string;
    label: string;
    module: 'SETTINGS';
    icon: JSX.Element;
  }> = [
    {
      label: t('settings'),
      href: '/settings',
      icon: <Settings />,
      module: 'SETTINGS',
    },
  ];

  const toggleSection = (key: string) => {
    // Si el sidebar está colapsado, expandirlo automáticamente
    if (isCollapsed) {
      setIsCollapsed(false);
      setIsMainNavCollapsed(false);
    }
    setOpenSections(prev => ({ ...prev, [key]: !prev[key] }));
  };

  useEffect(() => {
    const toggleTextOpacity = () => {
      setIsTextVisible(isCollapsed);
    };
    const timeoutId = setTimeout(toggleTextOpacity, 200);
    return () => clearTimeout(timeoutId);
  }, [isCollapsed]);

  return (
    <aside
      className={cn(
        'h-screen flex flex-col rounded-r-xl border-r border-slate-200 bg-white pt-3 shadow-md transition-all duration-300',
        isCollapsed ? 'w-16' : 'w-56',
      )}
    >
      {/* Logo and Toggle - Fixed at top */}
      <div
        className={cn(
          'flex items-center pb-4 transition-all duration-300',
          isCollapsed ? 'justify-center px-2' : 'justify-between px-3',
        )}
      >
        {!isCollapsed && <LogoWithLongPress brand={brand} isTextVisible={isTextVisible} />}

        <Button
          variant="ghost"
          size="icon"
          onClick={toggleSidebar}
          aria-label={isCollapsed ? t('expandSidebar') : t('collapseSidebar')}
          className={cn(
            'rounded-xl bg-slate-50 p-1 text-slate-600 transition-all hover:bg-slate-100 focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
          )}
        >
          {isCollapsed ? (
            <PanelLeftOpenIcon strokeWidth={1.5} />
          ) : (
            <PanelLeftCloseIcon strokeWidth={1.5} />
          )}
        </Button>
      </div>

      {/* Navigation - Scrollable */}
      <div className="flex-1 overflow-y-auto">
        <ul>
          {visibleNavigation.map((item, index) => {
            const hasChildren = item.children?.length;

            if (!hasChildren) {
              return (
                <NavigationLink
                  key={item.name}
                  href={item.href}
                  isActive={item.isActive}
                  isCollapsed={isCollapsed}
                  isTextVisible={isTextVisible}
                  linkText={item.name}
                >
                  <item.icon onClick={() => toggleSection(item.name)} strokeWidth={1.5} />
                </NavigationLink>
              );
            }

            const isOpen = openSections[item.name] ?? item.isActive; // abre si está activo
            const visibleChildren = item.children?.filter(child => canRead(child.module)) ?? [];
            const sectionId = `nav-section-${index}`;

            return (
              <section key={item.name}>
                {item.separator === 'top' && (
                  <div className="flex border-t border-slate-200 p-2"></div>
                )}
                <li key={item.name} className="px-2">
                  {/* Padre. Colapsado el toggle expande el sidebar, así que no expone estado de sección. */}
                  <button
                    type="button"
                    onClick={() => toggleSection(item.name)}
                    aria-expanded={isCollapsed ? undefined : isOpen}
                    aria-controls={isCollapsed ? undefined : sectionId}
                    className={cn(
                      'flex w-full items-center gap-3 rounded-xl px-3 py-2 text-slate-700 hover:bg-slate-50',
                      item.isActive && 'bg-slate-50',
                    )}
                  >
                    <item.icon strokeWidth={1.5} />
                    {isCollapsed && <span className="sr-only">{item.name}</span>}
                    {!isCollapsed && !isTextVisible && (
                      <>
                        <span className="flex-1 text-left text-sm font-medium">{item.name}</span>
                        <ChevronRightIcon
                          className={cn('h-4 w-4 transition-transform', isOpen && 'rotate-90')}
                        />
                      </>
                    )}
                  </button>

                  {!isCollapsed && !isTextVisible && isOpen && visibleChildren.length > 0 && (
                    <ul id={sectionId} className="mt-1 space-y-1 pl-9">
                      {visibleChildren.map(child => (
                        <Link to={child.href ?? '#'} key={child.name} className="block">
                          <div
                            className={cn(
                              'flex items-center gap-3 rounded-lg px-3 py-2 text-sm text-slate-600 hover:bg-slate-50',
                              child.isActive && 'bg-slate-100 text-slate-900 font-semibold',
                            )}
                          >
                            <child.icon strokeWidth={1.5} />

                            {child.name}
                          </div>
                        </Link>
                      ))}
                    </ul>
                  )}
                </li>
              </section>
            );
          })}
        </ul>
      </div>

      {/* Footer - Fixed at bottom */}
      <div className="mt-auto">
        <div
          className={cn(
            'flex border-t border-slate-200 p-2',
            isCollapsed ? 'justify-center' : 'justify-end',
          )}
        >
          {canSeeNotifications && <NotificationsBell compact={isCollapsed} />}
        </div>

        {/* User Switch */}
        <div className="flex items-center">
          <DropdownMenu dir={'rtl'}>
            <DropdownMenuTrigger
              asChild
              id="userDropdownTrigger"
              className="w-full rounded-br-xl py-4 transition-colors duration-200 hover:bg-slate-50 focus:outline-none"
            >
              <div
                className={cn(
                  'flex cursor-pointer flex-row items-center gap-3',
                  isCollapsed ? 'justify-center px-2' : 'px-4',
                )}
              >
                <ProfileAvatar avatarUrl={user?.avatarUrl} userName={user?.name} />
                {!isCollapsed && !isTextVisible && (
                  <>
                    <div
                      className={cn(
                        isTextVisible ? 'opacity-0' : 'opacity-100',
                        'grow overflow-hidden',
                      )}
                    >
                      <p
                        title={user?.name}
                        className={cn(
                          'ph-no-capture ph-no-capture -mb-0.5 truncate text-sm font-bold text-slate-700',
                        )}
                      >
                        {user?.name ? <span>{user?.name}</span> : <span>{user?.email}</span>}
                      </p>
                      <p className="text-sm text-slate-700">{user?.role}</p>
                    </div>
                    <ChevronRightIcon
                      className={cn('h-5 w-5 shrink-0 text-slate-700 hover:text-slate-500')}
                    />
                  </>
                )}
              </div>
            </DropdownMenuTrigger>

            <DropdownMenuContent
              id="userDropdownInnerContentWrapper"
              side="right"
              sideOffset={10}
              alignOffset={5}
              align="end"
            >
              {/* Dropdown Items */}

              {dropdownNavigation.map((link, index) => (
                <DropdownMenuItem
                  key={`dropdown-item-${index}`}
                  onClick={() => {
                    navigate(link.href);
                  }}
                  icon={link.icon}
                >
                  {link.label}
                </DropdownMenuItem>
              ))}
              {/* Logout */}
              <DropdownMenuItem
                onClick={async () => {
                  logout();
                  navigate('/auth/login');
                }}
                icon={<LogOutIcon strokeWidth={1.5} />}
              >
                {t('logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </aside>
  );
};
